import { useMemo, useState } from "react";
import { usePortal, type CAActivityRow, type CAClientProfile, type CACodeDomain, type CACodeValue, type CACodeValueUsage, type CAEditability, type CARelabel, type CASettingsCatalog, type CASettingsDomain, type ClientDetail } from "@app/portal-sdk";
import { Alert, AppBadge, Box, Button, DataTable, EmptyState, List, ListItemButton, ListItemText, Stack, Switch, TextField, Typography } from "@app/ui-kit";
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { LoadingScreen } from "../../components/LoadingScreen";
import { formatDateTime } from "../../lib/format";
import { errorMessage, useApiQuery } from "../../lib/useApiQuery";

/**
 * Client administration (docs/platform/STORIES_ADMIN_CLIENT_SETTINGS.md, ADM-3 and
 * ADM-7). A section list, not a page of fields. The sections come from the
 * database's settings catalogue: the profile, the
 * client-wide vocabularies, one section per application that owns
 * vocabulary, the platform vocabulary shown greyed with the reason, and the
 * activity log. Nothing here decides which domains are editable; the
 * catalogue says.
 */
type Props = { client: ClientDetail; onChange: () => void };

const LEVEL: Record<CAEditability, number> = { label_only: 0, curated: 1, open: 2 };

type SectionKey = { kind: "profile" } | { kind: "domains"; scope: "client" | "app" | "platform"; owningApp?: string; title: string } | { kind: "renamed" } | { kind: "activity" };

export const SettingsTab = ({ client, onChange }: Props) => {
  const { api } = usePortal();
  const base = `/clients/${client.id}/apps/channel-analytics`;
  const enabled = client.enabledApps.includes("channel-analytics");
  const catalog = useApiQuery<CASettingsCatalog | null>(() => (enabled ? api.request<CASettingsCatalog>(`${base}/settings/catalog`) : Promise.resolve(null)), [api, base, enabled]);
  const [section, setSection] = useState<SectionKey>({ kind: "profile" });
  const [domain, setDomain] = useState<string | null>(null);

  const sections = useMemo<{ key: SectionKey; label: string; count?: number; muted?: boolean }[]>(() => {
    const c = catalog.data;
    if (!c) return [];
    const out: { key: SectionKey; label: string; count?: number; muted?: boolean }[] = [
      { key: { kind: "profile" }, label: "Client profile" },
      { key: { kind: "domains", scope: "client", title: "Reference data · client-wide" }, label: "Reference data · client-wide", count: c.domains.filter((d) => d.scope === "client").length }
    ];
    for (const app of c.apps.filter((a) => c.domains.some((d) => d.scope === "app" && d.owningApp === a.appCode))) {
      out.push({ key: { kind: "domains", scope: "app", owningApp: app.appCode, title: `Reference data · ${app.appName}` }, label: `Reference data · ${app.appName}`, count: c.domains.filter((d) => d.scope === "app" && d.owningApp === app.appCode).length });
    }
    out.push({ key: { kind: "domains", scope: "platform", title: "Platform vocabulary" }, label: "Platform vocabulary", count: c.domains.filter((d) => d.scope === "platform").length, muted: true });
    out.push({ key: { kind: "renamed" }, label: "Renamed values", count: c.renamedCount });
    out.push({ key: { kind: "activity" }, label: "Activity log" });
    return out;
  }, [catalog.data]);

  if (!enabled) {
    return <EmptyState eyebrow="Client administration" title="Enable Channel Analytics first" description="Client settings are held with the client's analytical data and are provisioned when Channel Analytics is enabled on the Apps tab." />;
  }
  if (catalog.loading && !catalog.data) return <LoadingScreen minHeight={240} />;
  if (catalog.error) return <Alert severity="error">{catalog.error}</Alert>;
  const c = catalog.data!;
  const same = (a: SectionKey, b: SectionKey) => JSON.stringify(a) === JSON.stringify(b);

  const domainsFor = (key: Extract<SectionKey, { kind: "domains" }>) => c.domains.filter((d) => d.scope === key.scope && (!key.owningApp || d.owningApp === key.owningApp));

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "260px 1fr" }, gap: 4, alignItems: "start" }}>
      <List dense sx={{ bgcolor: "background.paper", border: "1.5px solid", borderColor: "divider", borderRadius: "6px", py: 0.5 }}>
        {sections.map((s) => (
          <ListItemButton key={s.label} selected={same(s.key, section)} onClick={() => { setSection(s.key); setDomain(null); }} sx={s.muted ? { opacity: 0.7 } : undefined}>
            <ListItemText primary={s.label} secondary={s.count !== undefined ? (s.key.kind === "renamed" ? `${s.count} diverge${s.count === 1 ? "s" : ""} from the standard` : `${s.count} vocabular${s.count === 1 ? "y" : "ies"}`) : undefined} />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ minWidth: 0 }}>
        {section.kind === "profile" && <ProfileSection client={client} profile={c.profile} onSaved={() => { void catalog.reload(); onChange(); }} />}
        {section.kind === "domains" && (domain ? (
          <DomainEditor client={client} domain={domain} onBack={() => { setDomain(null); void catalog.reload(); }} />
        ) : (
          <DomainList title={section.title} scope={section.scope} domains={domainsFor(section)} onOpen={setDomain} />
        ))}
        {section.kind === "renamed" && <RenamedSection client={client} onChanged={() => void catalog.reload()} />}
        {section.kind === "activity" && <ActivitySection client={client} />}
      </Box>
    </Box>
  );
};

const ProfileSection = ({ client, profile, onSaved }: { client: ClientDetail; profile: CAClientProfile; onSaved: () => void }) => {
  const { api } = usePortal();
  const base = `/clients/${client.id}/apps/channel-analytics`;
  const [form, setForm] = useState({ tenantName: profile.tenantName, industry: profile.industry ?? "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string[] | null>(null);
  const dirty = form.tenantName !== profile.tenantName || form.industry !== (profile.industry ?? "");
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.request<{ profile: CAClientProfile; changed: string[] }>(`${base}/settings/profile`, { method: "PUT", json: { ...form, industry: form.industry.trim() || null, reason: reason.trim() } });
      setSaved(r.changed); setReason(""); onSaved();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ letterSpacing: 0, textTransform: "none" }}>Client profile</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
        The client as Channel Analytics names it: the name and the industry. Client code <code>{profile.tenantCode}</code>{profile.currentFiscalYear ? <>; today is in <strong>{profile.currentFiscalYear.label}</strong></> : null}. The fiscal calendar, the currency and the locale are on the client's own row, in the Settings section of the Overview tab (REF-7).
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" onClose={() => setSaved(null)}>{saved.length ? `Saved. Changed: ${saved.join(", ")}.` : "Saved. Nothing changed, so nothing was logged."}</Alert>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Client name" value={form.tenantName} onChange={(e) => setForm({ ...form, tenantName: e.target.value })} sx={{ minWidth: 280 }} />
        <TextField label="Industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} sx={{ minWidth: 240 }} />
      </Stack>
      <TextField label="Reason" helperText="Recorded in the client's activity log with your name. Required." value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline minRows={2} sx={{ maxWidth: 820 }} />
      <Box><Button variant="contained" onClick={() => void save()} disabled={!dirty || reason.trim().length < 3 || busy}>Save profile</Button></Box>
    </Stack>
  );
};

const LEVEL_LABEL: Record<CAEditability, string> = { label_only: "Relabel and reorder", curated: "Relabel, reorder, retire and restore", open: "Relabel, reorder, retire, restore and add" };
const REASON_LABEL: Record<string, string> = {
  branched_on: "Something computes on the codes", default_value: "The schema writes a default from the codes", check_constrained: "A check enumerates the codes",
  platform_policy: "Locked by policy", structural_pending: "Locked pending arithmetic not yet built", unconstrained: "Nothing reads the codes"
};

const DomainList = ({ title, scope, domains, onOpen }: { title: string; scope: CASettingsDomain["scope"]; domains: CASettingsDomain[]; onOpen: (d: string) => void }) => (
  <Stack spacing={2}>
    <Typography variant="h6" sx={{ letterSpacing: 0, textTransform: "none" }}>{title}</Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
      {scope === "platform"
        ? "Vocabulary that ships with the product. The analytics are written against these codes, so a client may change the words a screen shows and the order they come in, and nothing else. Each row says which rule decided."
        : scope === "client"
          ? "Vocabulary that means the same thing in every application. What may be changed depends on what the schema does with the codes; each row says how far it goes and why."
          : "Vocabulary this application gives meaning to. What may be changed depends on what the schema does with the codes; each row says how far it goes and why."}
    </Typography>
    {domains.length === 0 ? <Typography variant="body2" sx={{ color: "text.disabled" }}>Nothing here.</Typography> : (
      <DataTable
        columns={[{ key: "name", label: "Vocabulary" }, { key: "values", label: "Values" }, { key: "client", label: "Client-added" }, { key: "renamed", label: "Renamed" }, { key: "retired", label: "Retired" }, { key: "may", label: "A client may" }, { key: "why", label: "Because" }, { key: "edit", label: "" }]}
        rows={domains.map((d) => ({
          name: <Box><Typography variant="body2" sx={{ color: scope === "platform" ? "text.secondary" : "text.primary" }}>{d.label}</Typography>{d.description && <Typography variant="caption" sx={{ color: "text.disabled" }}>{d.description}</Typography>}</Box>,
          values: d.valueCount, client: d.clientAddedCount, renamed: d.renamedCount, retired: d.retiredCount,
          may: <AppBadge label={d.editability.replace("_", " ")} size="small" variant={d.editability === "open" ? "filled" : "outlined"} color={d.editability === "open" ? "success" : d.editability === "curated" ? "info" : "default"} />,
          why: <Tooltip title={d.reasonText ?? ""}><Typography component="span" variant="caption" sx={{ color: "text.secondary", cursor: d.reasonText ? "help" : "default" }}>{REASON_LABEL[d.reason] ?? d.reason}</Typography></Tooltip>,
          edit: <Link component="button" underline="hover" onClick={() => onOpen(d.domain)}>Manage</Link>
        }))}
      />
    )}
  </Stack>
);

const DomainEditor = ({ client, domain, onBack }: { client: ClientDetail; domain: string; onBack: () => void }) => {
  const { api } = usePortal();
  const base = `/clients/${client.id}/apps/channel-analytics/settings/domains/${domain}`;
  const values = useApiQuery<CACodeDomain>(() => api.request(base), [api, base]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, { label: string; description: string }>>({});
  const [add, setAdd] = useState({ code: "", label: "", description: "" });
  const [retiring, setRetiring] = useState<{ value: CACodeValue; usage: CACodeValueUsage | null } | null>(null);

  const patch = async (code: string, body: Record<string, unknown>) => {
    setBusy(code); setError(null);
    try {
      const row = await api.request<CACodeValue>(`${base}/values/${encodeURIComponent(code)}`, { method: "PATCH", json: body });
      values.setData((d) => (d ? { ...d, values: d.values.map((v) => (v.code === code ? row : v)) } : d));
      setDraft((prev) => { const next = { ...prev }; delete next[code]; return next; });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(null); }
  };
  const create = async () => {
    setBusy("__add"); setError(null);
    try {
      const row = await api.request<CACodeValue>(`${base}/values`, { method: "POST", json: { code: add.code, label: add.label, description: add.description || null } });
      values.setData((d) => (d ? { ...d, values: [...d.values, row] } : d));
      setAdd({ code: "", label: "", description: "" });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(null); }
  };
  // Retiring states its impact before it is confirmed: how many rows carry the value today.
  const askRetire = async (v: CACodeValue) => {
    setRetiring({ value: v, usage: null });
    try {
      const usage = await api.request<CACodeValueUsage>(`${base}/values/${encodeURIComponent(v.code)}/usage`);
      setRetiring({ value: v, usage });
    } catch (err) { setError(errorMessage(err)); setRetiring(null); }
  };
  const plural = (entity: string, n: number) => { const word = entity.replace(/_/g, " "); return n === 1 ? word : word.endsWith("s") ? word : `${word}s`; };

  if (values.loading && !values.data) return <LoadingScreen minHeight={200} />;
  if (values.error && !values.data) return <Alert severity="error">{values.error}</Alert>;
  const d = values.data!;
  const level = LEVEL[d.editability];
  const rows = d.values;
  return (
    <Stack spacing={2}>
      <Link component="button" underline="hover" variant="body2" onClick={onBack} sx={{ alignSelf: "flex-start" }}>← Back</Link>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ letterSpacing: 0, textTransform: "none" }}>{d.label}</Typography>
        <AppBadge label={d.editability.replace("_", " ")} size="small" variant={d.editability === "open" ? "filled" : "outlined"} color={d.editability === "open" ? "success" : d.editability === "curated" ? "info" : "default"} />
      </Stack>
      {d.description && <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>{d.description}</Typography>}
      <Alert severity={d.editability === "open" ? "success" : "info"} icon={false}>
        <strong>{LEVEL_LABEL[d.editability]}.</strong> {REASON_LABEL[d.reason] ?? d.reason}{d.reasonText ? `: ${d.reasonText}.` : "."}
        {" "}A retired value keeps rendering on the rows that already use it. A value the client edits becomes the client's, so re-seeding the model's vocabulary never reverts it.
      </Alert>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <DataTable
        columns={[{ key: "code", label: "Code" }, { key: "label", label: "Label" }, { key: "description", label: "Description" }, { key: "order", label: "Order" }, { key: "origin", label: "Origin" }, { key: "active", label: "In use" }, { key: "save", label: "" }]}
        rows={rows.map((v) => {
          const dr = draft[v.code] ?? { label: v.label, description: v.description ?? "" };
          const changed = dr.label !== v.label || dr.description !== (v.description ?? "");
          return {
            code: <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12, color: v.isActive ? "text.secondary" : "text.disabled" }}>{v.code}</Typography>,
            label: (
              <Box>
                <TextField size="small" value={dr.label} onChange={(e) => setDraft({ ...draft, [v.code]: { ...dr, label: e.target.value } })} sx={{ minWidth: 200 }} />
                {v.renamed && (
                  <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.5 }}>
                    renamed from <em>{v.systemLabel}</em> · <Link component="button" underline="hover" variant="caption" onClick={() => void patch(v.code, { revert: true })} disabled={busy === v.code}>revert to standard</Link>
                  </Typography>
                )}
              </Box>
            ),
            description: <TextField size="small" value={dr.description} onChange={(e) => setDraft({ ...draft, [v.code]: { ...dr, description: e.target.value } })} sx={{ minWidth: 240 }} />,
            order: <TextField size="small" type="number" value={v.sortOrder} onChange={(e) => void patch(v.code, { sortOrder: Number(e.target.value) })} sx={{ width: 80 }} />,
            origin: <AppBadge label={v.isSystem ? "model" : "client"} size="small" variant={v.isSystem ? "filled" : "outlined"} />,
            active: level >= 1 ? (
              <Switch size="small" checked={v.isActive} onChange={(e) => (e.target.checked ? void patch(v.code, { isActive: true }) : void askRetire(v))} disabled={busy === v.code} inputProps={{ "aria-label": `${v.isActive ? "Retire" : "Restore"} ${v.label}` }} />
            ) : (
              <Tooltip title={`Cannot be retired: ${d.reasonText ?? REASON_LABEL[d.reason] ?? d.reason}`}><Box component="span"><Switch size="small" checked={v.isActive} disabled /></Box></Tooltip>
            ),
            save: changed ? <Button size="small" variant="outlined" onClick={() => void patch(v.code, { label: dr.label, description: dr.description || null })} disabled={busy === v.code}>Save</Button> : ""
          };
        })}
      />
      {level >= 2 ? (
        <Box sx={{ p: 2, border: "1px dashed", borderColor: "divider", borderRadius: "6px" }}>
          <Typography variant="overline" sx={{ color: "secondary.main", display: "block", mb: 1 }}>Add a value</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
            <TextField size="small" label="Code" helperText="lower-case, stored on the row" value={add.code} onChange={(e) => setAdd({ ...add, code: e.target.value })} sx={{ minWidth: 180 }} />
            <TextField size="small" label="Label" value={add.label} onChange={(e) => setAdd({ ...add, label: e.target.value })} sx={{ minWidth: 220 }} />
            <TextField size="small" label="Description" value={add.description} onChange={(e) => setAdd({ ...add, description: e.target.value })} sx={{ minWidth: 260 }} />
            <Button variant="contained" onClick={() => void create()} disabled={!add.code.trim() || !add.label.trim() || busy === "__add"}>Add</Button>
          </Stack>
          <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 1 }}>The collection workbook's dropdown for this vocabulary warns rather than blocks, so a value added here can be typed into the sheet; the loader checks it against this list.</Typography>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>Values cannot be added to this vocabulary. {d.editability === "curated" ? "A check in the schema enumerates the codes; a new one is a model change and a release." : "Something in the schema or the analytics is written against these codes."}</Typography>
      )}
      <Dialog open={retiring !== null} onClose={() => setRetiring(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Retire {retiring?.value.label}?</DialogTitle>
        <DialogContent>
          {retiring?.usage === null ? <LoadingScreen minHeight={80} /> : (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              {retiring && retiring.usage && (retiring.usage.total === 0 ? (
                <Typography variant="body2">Nothing uses <strong>{retiring.value.label}</strong> today. Retiring it removes it from the dropdowns; it can be restored.</Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    {retiring.usage.tables.filter((t) => t.rows > 0).map((t) => `${t.rows} ${plural(t.entity, t.rows)}`).join(", ")} use <strong>{retiring.value.label}</strong>.
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>Those rows keep the value and keep rendering it. It stops being offered for new rows, and can be restored.</Typography>
                </>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setRetiring(null)}>Keep it</Button>
          <Button variant="contained" color="warning" disabled={!retiring || retiring.usage === null || busy !== null} onClick={() => { const v = retiring!.value; setRetiring(null); void patch(v.code, { isActive: false }); }}>Retire</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

/** ADM-13: every value whose label has diverged from the standard word, so divergence is visible at a glance. */
const RenamedSection = ({ client, onChanged }: { client: ClientDetail; onChanged: () => void }) => {
  const { api } = usePortal();
  const base = `/clients/${client.id}/apps/channel-analytics/settings`;
  const rows = useApiQuery<{ rows: CARelabel[] }>(() => api.request(`${base}/relabels`), [api, base]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const revert = async (r: CARelabel) => {
    setBusy(`${r.domain}.${r.code}`); setError(null);
    try {
      await api.request(`${base}/domains/${r.domain}/values/${encodeURIComponent(r.code)}`, { method: "PATCH", json: { revert: true } });
      await rows.reload(); onChanged();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(null); }
  };
  if (rows.loading && !rows.data) return <LoadingScreen minHeight={200} />;
  const list = rows.data?.rows ?? [];
  return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ letterSpacing: 0, textTransform: "none" }}>Renamed values</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
        Labels are the client's words and codes are ours, so any value may be renamed. This is every value whose word has diverged from the standard, across all vocabularies, so you can see how far this client's language has drifted from the method. Revert restores the standard word and lets the model's seed govern the row again.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {list.length === 0 ? <Typography variant="body2" sx={{ color: "text.disabled" }}>Nothing renamed. Every value carries its standard word.</Typography> : (
        <DataTable
          columns={[{ key: "domain", label: "Vocabulary" }, { key: "code", label: "Code" }, { key: "standard", label: "Standard" }, { key: "client", label: "Client's word" }, { key: "active", label: "In use" }, { key: "revert", label: "" }]}
          rows={list.map((r) => ({
            domain: r.domainLabel, code: <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>{r.code}</Typography>,
            standard: r.systemLabel, client: <strong>{r.label}</strong>, active: r.isActive ? "yes" : "retired",
            revert: <Button size="small" variant="text" onClick={() => void revert(r)} disabled={busy === `${r.domain}.${r.code}`}>Revert to standard</Button>
          }))}
        />
      )}
    </Stack>
  );
};

const ActivitySection = ({ client }: { client: ClientDetail }) => {
  const { api } = usePortal();
  const base = `/clients/${client.id}/apps/channel-analytics`;
  const activity = useApiQuery<{ rows: CAActivityRow[] }>(() => api.request(`${base}/fiscal/activity`), [api, base]);
  if (activity.loading && !activity.data) return <LoadingScreen minHeight={200} />;
  const rows = activity.data?.rows ?? [];
  return (
    <Stack spacing={2}>
      <Typography variant="h6" sx={{ letterSpacing: 0, textTransform: "none" }}>Activity log</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>Every change to this client's settings, written by the database itself and visible to the client: what changed, who did it, from where, and why.</Typography>
      {rows.length === 0 ? <Typography variant="body2" sx={{ color: "text.disabled" }}>No settings changes recorded for this client.</Typography> : (
        <DataTable
          columns={[{ key: "when", label: "When" }, { key: "what", label: "Setting" }, { key: "change", label: "Change" }, { key: "who", label: "By" }, { key: "why", label: "Why" }]}
          rows={rows.map((r) => ({
            when: formatDateTime(r.changedAt), what: r.settingKey, change: `${JSON.stringify(r.oldValue)} → ${JSON.stringify(r.newValue)}`,
            who: `${r.actor} (${r.changeSource.replace("_", " ")})`, why: r.reason ?? "—"
          }))}
        />
      )}
    </Stack>
  );
};
